import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { NoteHtml, sanitizeNoteHtml } from '@/components/editor/BasicRichTextEditor';
import { cn } from '@/lib/utils';

export interface ScheduleContentTab {
  id?: string;
  title: string;
  body_html: string | null;
}

export function useVisibleScheduleTabs(eventId?: string) {
  return useQuery({
    queryKey: ['visible-event-schedule-tabs', eventId],
    enabled: !!eventId,
    staleTime: 0,
    queryFn: async (): Promise<ScheduleContentTab[]> => {
      const { data, error } = await (supabase as any).from('event_schedule_tabs')
        .select('id, title, body_html')
        .eq('event_id', eventId).eq('is_visible', true)
        .order('sort_order', { ascending: true }).order('created_at', { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });
}

export function ScheduleContentTabs({ tabs }: { tabs: ScheduleContentTab[] }) {
  const [active, setActive] = useState(0);
  const [fade, setFade] = useState({ left: false, right: false });
  const stripRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const index = tabs.length ? Math.min(active, tabs.length - 1) : 0;

  const updateFade = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setFade({ left: el.scrollLeft > 1, right: el.scrollLeft < max - 1 });
  }, []);

  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    updateFade();
    const ro = new ResizeObserver(updateFade);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateFade, tabs.length]);

  useEffect(() => {
    const strip = stripRef.current;
    const tab = strip?.querySelector<HTMLElement>(`[data-tab-index="${index}"]`);
    if (!strip || !tab) return;
    const left = tab.offsetLeft - strip.offsetLeft;
    const right = left + tab.offsetWidth;
    if (left < strip.scrollLeft) strip.scrollTo({ left: Math.max(0, left - 32), behavior: 'smooth' });
    else if (right > strip.scrollLeft + strip.clientWidth)
      strip.scrollTo({ left: right - strip.clientWidth + 32, behavior: 'smooth' });
  }, [index, tabs.length]);

  if (!tabs.length) return null;
  const content = new DOMParser().parseFromString(sanitizeNoteHtml(tabs[index].body_html), 'text/html');
  content.querySelectorAll('a').forEach((link) => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });
  return (
    <div className="mt-4 rounded-lg border border-border min-w-0">
      <div className="relative border-b border-border">
        <div ref={stripRef} onScroll={updateFade} role="tablist" aria-label="Event schedule information"
          className="schedule-tab-strip relative flex overflow-x-auto">
          {tabs.map((tab, i) => (
            <Button key={tab.id || i} variant="ghost" role="tab" id={`${id}-tab-${i}`} data-tab-index={i}
              aria-selected={i === index} aria-controls={`${id}-panel`} onClick={() => setActive(i)}
              className={cn('shrink-0 whitespace-nowrap rounded-none border-b-2 px-4 py-2 text-sm font-medium',
                i === index ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground')}>
              {tab.title}
            </Button>
          ))}
        </div>
        <div aria-hidden className={cn('pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-background to-transparent transition-opacity',
          fade.left ? 'opacity-100' : 'opacity-0')} />
        <div aria-hidden className={cn('pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent transition-opacity',
          fade.right ? 'opacity-100' : 'opacity-0')} />
      </div>
      <div id={`${id}-panel`} role="tabpanel" aria-labelledby={`${id}-tab-${index}`} className="p-4 break-words [&_a]:break-all">
        <NoteHtml html={content.body.innerHTML} className="text-foreground [&_a]:text-primary" />
      </div>
    </div>
  );
}

export function EventScheduleTabs({ eventId }: { eventId: string }) {
  const { data: tabs = [] } = useVisibleScheduleTabs(eventId);
  return <ScheduleContentTabs tabs={tabs} />;
}
